// Home Stock jar icons for full / half / low / empty — matches Banani inventory result design.

import { useId } from 'react';
import { INVENTORY_LEVEL, type InventoryLevel } from '@fdh/shared';
import { PressableHint } from '../ui/PressableHint';

type HomeStockLevelIconSize = 'sm' | 'md' | 'lg';

type HomeStockLevelIconProps = {
  level: InventoryLevel | null | undefined;
  size?: HomeStockLevelIconSize;
  className?: string;
  ariaLabel?: string;
  /** When set, shows tap/hover detail below the icon (e.g. stock level label). */
  tooltipLabel?: string;
  /** Optional stable id for PressableHint scope; auto-generated when omitted. */
  hintId?: string;
  /** When true and level is empty, blinks the red strip (~1s on / ~1s off). */
  blinkWhenEmpty?: boolean;
};

const SIZE_PX: Record<HomeStockLevelIconSize, { width: number; height: number }> = {
  sm: { width: 20, height: 28 },
  md: { width: 28, height: 40 },
  lg: { width: 40, height: 56 },
};

const JarOutline = () => (
  <>
    <rect x="2" y="4" width="24" height="34" rx="4" fill="#F7FAFC" stroke="#1A202C" strokeWidth="2" />
    <path d="M8 2h12v2H8V2z" fill="#1A202C" />
    <circle cx="10" cy="16" r="2.5" fill="#1A202C" />
    <circle cx="18" cy="16" r="2.5" fill="#1A202C" />
  </>
);

const FullJar = () => (
  <>
    <rect x="2" y="4" width="24" height="34" rx="4" fill="#38A169" stroke="#1A202C" strokeWidth="2" />
    <path d="M8 2h12v2H8V2z" fill="#1A202C" />
    <circle cx="10" cy="16" r="2.5" fill="#1A202C" />
    <circle cx="18" cy="16" r="2.5" fill="#1A202C" />
    <path
      d="M11 20c1.5 2 4.5 2 6 0"
      stroke="#1A202C"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
    <rect x="4" y="6" width="4" height="30" rx="2" fill="white" fillOpacity="0.3" />
  </>
);

const HalfJar = () => (
  <>
    <JarOutline />
    <rect x="3" y="18" width="22" height="19" rx="3" fill="#ECC94B" />
    <path d="M12 21h4" stroke="#1A202C" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <rect x="4" y="20" width="3" height="15" rx="1.5" fill="white" fillOpacity="0.4" />
  </>
);

const LowJar = () => (
  <>
    <JarOutline />
    <rect x="3" y="28" width="22" height="9" rx="3" fill="#DD6B20" />
    <path
      d="M11 22c1.5 -1.5 4.5 -1.5 6 0"
      stroke="#1A202C"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
    <rect x="4" y="29" width="3" height="7" rx="1.5" fill="white" fillOpacity="0.4" />
  </>
);

const EmptyJar = ({ blinkRed }: { blinkRed?: boolean }) => (
  <>
    <JarOutline />
    <rect
      x="3"
      y="32"
      width="22"
      height="5"
      rx="2"
      fill="#E53E3E"
      className={blinkRed ? 'empty-jar-red-blink' : undefined}
    />
    <path
      d="M11 23c1.5 -2 4.5 -2 6 0"
      stroke="#1A202C"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
  </>
);

const UnknownJar = () => (
  <>
    <rect x="2" y="4" width="24" height="34" rx="4" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="2" />
    <path d="M8 2h12v2H8V2z" fill="#94A3B8" />
    <circle cx="10" cy="16" r="2.5" fill="#94A3B8" />
    <circle cx="18" cy="16" r="2.5" fill="#94A3B8" />
    <path d="M11 21h6" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" fill="none" />
  </>
);

const renderJarByLevel = (
  level: InventoryLevel | null | undefined,
  blinkWhenEmpty: boolean,
) => {
  if (level === INVENTORY_LEVEL.FULL) {
    return <FullJar />;
  }
  if (level === INVENTORY_LEVEL.HALF) {
    return <HalfJar />;
  }
  if (level === INVENTORY_LEVEL.LOW) {
    return <LowJar />;
  }
  if (level === INVENTORY_LEVEL.EMPTY) {
    return <EmptyJar blinkRed={blinkWhenEmpty} />;
  }
  return <UnknownJar />;
};

export const HomeStockLevelIcon = ({
  level,
  size = 'md',
  className = '',
  ariaLabel,
  tooltipLabel,
  hintId: hintIdProp,
  blinkWhenEmpty = false,
}: HomeStockLevelIconProps) => {
  const autoHintId = useId();
  const hintId = hintIdProp ?? `stock-level-${autoHintId}`;
  const { width, height } = SIZE_PX[size];
  const accessibleLabel = ariaLabel ?? tooltipLabel;
  const svg = (
    <svg
      width={width}
      height={height}
      viewBox="0 0 28 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`block shrink-0 ${className}`.trim()}
      aria-hidden={accessibleLabel ? true : undefined}
    >
      {renderJarByLevel(level, blinkWhenEmpty)}
    </svg>
  );

  if (!tooltipLabel) {
    return (
      <span className="inline-flex shrink-0" role="img" aria-label={accessibleLabel}>
        {svg}
      </span>
    );
  }

  return (
    <PressableHint
      hintId={hintId}
      detail={tooltipLabel}
      ariaLabel={accessibleLabel ?? tooltipLabel}
      placement="top"
      triggerClassName="shrink-0 border-0 bg-transparent p-0"
    >
      {svg}
    </PressableHint>
  );
};
